import { useState } from "react";

import { ToggleInput, useToast } from "@a2z/ui";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import { BostaSheet } from "@/modules/settings/components/BostaSheet";
import {
  useBostaConnectionQuery,
  useUpdateBostaActiveMutation,
} from "@/modules/settings/services/useBostaConnection";
import { IntegrationTile } from "./IntegrationTile";
import { BostaLogo } from "./logos/BostaLogo";

type Props = {
  storeSlug: string;
};

export function BostaIntegrationTile({ storeSlug }: Props) {
  const t = useTranslations();
  const toast = useToast();
  const { connection, platformEnabled, isLoading } = useBostaConnectionQuery({
    storeSlug,
  });
  const { mutateAsync: updateActive, isPending: isTogglingActive } =
    useUpdateBostaActiveMutation();
  const [sheetOpen, setSheetOpen] = useState(false);

  // Platform kill-switch: hide Bosta entirely when the admin disabled it.
  if (!platformEnabled) {
    return null;
  }

  const status = isLoading
    ? "loading"
    : !connection
      ? "disconnected"
      : connection.isActive
        ? "connected"
        : "paused";

  const statusLabel = !connection
    ? t(keys.bosta.statusNotConnected)
    : connection.isActive
      ? t(keys.bosta.statusConnected)
      : t(keys.bosta.statusPaused);

  const handleToggle = async (next: boolean) => {
    try {
      await updateActive({ storeSlug, isActive: next });
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : t(keys.errors.generic),
      );
    }
  };

  const footer = connection ? (
    <>
      <span className="text-xs text-[color:var(--text-secondary)]">
        {connection.isActive
          ? t(keys.bosta.pauseAction)
          : t(keys.bosta.resumeAction)}
      </span>
      <ToggleInput
        name="bosta-active"
        checked={connection.isActive}
        disabled={isTogglingActive}
        onChange={(event) => handleToggle(event.target.checked)}
        ariaLabel={
          connection.isActive
            ? t(keys.bosta.pauseAction)
            : t(keys.bosta.resumeAction)
        }
      />
    </>
  ) : undefined;

  return (
    <>
      <IntegrationTile
        name={t(keys.bosta.brandName)}
        description={t(keys.bosta.description)}
        logo={<BostaLogo />}
        status={status}
        statusLabel={statusLabel}
        onClick={() => setSheetOpen(true)}
        footer={footer}
      />
      <BostaSheet
        storeSlug={storeSlug}
        isOpen={sheetOpen}
        onClose={() => setSheetOpen(false)}
      />
    </>
  );
}
